import { createSlice } from '@reduxjs/toolkit';
import { AccessControlState } from './types';
import {
  fetchOrgRoles,
  createOrgRole,
  fetchSubscribedModules,
  fetchRoleMappings,
  saveRoleModuleMapping,
  fetchOrgStaff,
  createOrgStaff,
  deleteOrgStaff,
  updateOrgStaffStatus,
  updateOrgStaffRole,
} from './actions';

const initialState: AccessControlState = {
  orgRoles: [],
  orgRolesLoading: false,
  orgRolesError: null,

  subscribedModules: [],
  subscribedModulesLoading: false,
  subscribedModulesError: null,

  roleMappings: [],
  roleMappingsLoading: false,
  roleMappingsError: null,

  orgStaff: [],
  orgStaffLoading: false,
  orgStaffError: null,

  createOrgRoleLoading: false,
  createOrgRoleSuccess: false,
  createOrgRoleError: null,

  updateOrgRoleLoading: false,
  updateOrgRoleSuccess: false,
  updateOrgRoleError: null,

  deleteOrgRoleLoading: false,
  deleteOrgRoleSuccess: false,
  deleteOrgRoleError: null,

  saveMappingLoading: false,
  saveMappingSuccess: false,
  saveMappingError: null,

  createOrgStaffLoading: false,
  createOrgStaffSuccess: false,
  createOrgStaffError: null,

  deleteOrgStaffLoading: false,
  deleteOrgStaffSuccess: false,
  deleteOrgStaffError: null,

  updateOrgStaffStatusLoading: false,
  updateOrgStaffStatusSuccess: false,
  updateOrgStaffStatusError: null,

  updateOrgStaffRoleLoading: false,
  updateOrgStaffRoleSuccess: false,
  updateOrgStaffRoleError: null,

  actionMessage: null,
};

const accessControlSlice = createSlice({
  name: 'accessControl',
  initialState,
  reducers: {
    resetAccessControlActionState: (state) => {
      state.createOrgRoleSuccess = false;
      state.createOrgRoleError = null;
      state.updateOrgRoleSuccess = false;
      state.updateOrgRoleError = null;
      state.deleteOrgRoleSuccess = false;
      state.deleteOrgRoleError = null;
      state.saveMappingSuccess = false;
      state.saveMappingError = null;
      state.createOrgStaffSuccess = false;
      state.createOrgStaffError = null;
      state.deleteOrgStaffSuccess = false;
      state.deleteOrgStaffError = null;
      state.updateOrgStaffStatusSuccess = false;
      state.updateOrgStaffStatusError = null;
      state.updateOrgStaffRoleSuccess = false;
      state.updateOrgStaffRoleError = null;
      state.actionMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Org Roles
    builder.addCase(fetchOrgRoles.pending, (state) => {
      state.orgRolesLoading = true;
      state.orgRolesError = null;
    });
    builder.addCase(fetchOrgRoles.fulfilled, (state, action) => {
      state.orgRolesLoading = false;
      state.orgRoles = action.payload;
    });
    builder.addCase(fetchOrgRoles.rejected, (state, action) => {
      state.orgRolesLoading = false;
      state.orgRolesError = action.payload as string;
    });

    // Create Org Role
    builder.addCase(createOrgRole.pending, (state) => {
      state.createOrgRoleLoading = true;
      state.createOrgRoleSuccess = false;
      state.createOrgRoleError = null;
    });
    builder.addCase(createOrgRole.fulfilled, (state) => {
      state.createOrgRoleLoading = false;
      state.createOrgRoleSuccess = true;
      state.actionMessage = 'Role created successfully';
    });
    builder.addCase(createOrgRole.rejected, (state, action) => {
      state.createOrgRoleLoading = false;
      state.createOrgRoleError = action.payload as string;
    });

    // Fetch Subscribed Modules
    builder.addCase(fetchSubscribedModules.pending, (state) => {
      state.subscribedModulesLoading = true;
      state.subscribedModulesError = null;
    });
    builder.addCase(fetchSubscribedModules.fulfilled, (state, action) => {
      state.subscribedModulesLoading = false;
      state.subscribedModules = action.payload;
    });
    builder.addCase(fetchSubscribedModules.rejected, (state, action) => {
      state.subscribedModulesLoading = false;
      state.subscribedModulesError = action.payload as string;
    });

    // Fetch Role Mappings
    builder.addCase(fetchRoleMappings.pending, (state) => {
      state.roleMappingsLoading = true;
      state.roleMappingsError = null;
    });
    builder.addCase(fetchRoleMappings.fulfilled, (state, action) => {
      state.roleMappingsLoading = false;
      state.roleMappings = action.payload;
    });
    builder.addCase(fetchRoleMappings.rejected, (state, action) => {
      state.roleMappingsLoading = false;
      state.roleMappingsError = action.payload as string;
    });

    // Save Mapping
    builder.addCase(saveRoleModuleMapping.pending, (state) => {
      state.saveMappingLoading = true;
      state.saveMappingSuccess = false;
      state.saveMappingError = null;
    });
    builder.addCase(saveRoleModuleMapping.fulfilled, (state) => {
      state.saveMappingLoading = false;
      state.saveMappingSuccess = true;
    });
    builder.addCase(saveRoleModuleMapping.rejected, (state, action) => {
      state.saveMappingLoading = false;
      state.saveMappingError = action.payload as string;
    });

    // Fetch Org Staff
    builder.addCase(fetchOrgStaff.pending, (state) => {
      state.orgStaffLoading = true;
      state.orgStaffError = null;
    });
    builder.addCase(fetchOrgStaff.fulfilled, (state, action) => {
      state.orgStaffLoading = false;
      state.orgStaff = action.payload;
    });
    builder.addCase(fetchOrgStaff.rejected, (state, action) => {
      state.orgStaffLoading = false;
      state.orgStaffError = action.payload as string;
    });

    // Create Org Staff
    builder.addCase(createOrgStaff.pending, (state) => {
      state.createOrgStaffLoading = true;
      state.createOrgStaffSuccess = false;
      state.createOrgStaffError = null;
    });
    builder.addCase(createOrgStaff.fulfilled, (state) => {
      state.createOrgStaffLoading = false;
      state.createOrgStaffSuccess = true;
      state.actionMessage = 'Staff user created successfully';
    });
    builder.addCase(createOrgStaff.rejected, (state, action) => {
      state.createOrgStaffLoading = false;
      state.createOrgStaffError = action.payload as string;
    });

    // Delete Org Staff
    builder.addCase(deleteOrgStaff.pending, (state) => {
      state.deleteOrgStaffLoading = true;
      state.deleteOrgStaffSuccess = false;
      state.deleteOrgStaffError = null;
    });
    builder.addCase(deleteOrgStaff.fulfilled, (state) => {
      state.deleteOrgStaffLoading = false;
      state.deleteOrgStaffSuccess = true;
      state.actionMessage = 'Staff removed successfully';
    });
    builder.addCase(deleteOrgStaff.rejected, (state, action) => {
      state.deleteOrgStaffLoading = false;
      state.deleteOrgStaffError = action.payload as string;
    });

    // Update Org Staff Status
    builder.addCase(updateOrgStaffStatus.pending, (state) => {
      state.updateOrgStaffStatusLoading = true;
      state.updateOrgStaffStatusSuccess = false;
      state.updateOrgStaffStatusError = null;
    });
    builder.addCase(updateOrgStaffStatus.fulfilled, (state) => {
      state.updateOrgStaffStatusLoading = false;
      state.updateOrgStaffStatusSuccess = true;
      state.actionMessage = 'Staff status updated successfully';
    });
    builder.addCase(updateOrgStaffStatus.rejected, (state, action) => {
      state.updateOrgStaffStatusLoading = false;
      state.updateOrgStaffStatusError = action.payload as string;
    });

    // Update Org Staff Role
    builder.addCase(updateOrgStaffRole.pending, (state) => {
      state.updateOrgStaffRoleLoading = true;
      state.updateOrgStaffRoleSuccess = false;
      state.updateOrgStaffRoleError = null;
    });
    builder.addCase(updateOrgStaffRole.fulfilled, (state) => {
      state.updateOrgStaffRoleLoading = false;
      state.updateOrgStaffRoleSuccess = true;
      state.actionMessage = 'Staff role updated successfully';
    });
    builder.addCase(updateOrgStaffRole.rejected, (state, action) => {
      state.updateOrgStaffRoleLoading = false;
      state.updateOrgStaffRoleError = action.payload as string;
    });
  },
});

export const { resetAccessControlActionState } = accessControlSlice.actions;
export default accessControlSlice.reducer;
